import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Class,
  ClassWithdrawalRequest,
  Enrollment,
  ENROLLMENT_STATUS,
  Student,
  Teacher,
  WITHDRAWAL_REQUEST_STATUS,
} from "../models";
import { notificationService } from "../services/notification-service";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
  files?: Express.Multer.File[];
}

class ClassWithdrawalController {
  /**
   * Student JWTs do not include schoolId; resolve from the approved Student profile.
   */
  private async getApprovedStudentForWithdrawal(userId: string) {
    return Student.findOne({
      userId: new mongoose.Types.ObjectId(userId),
      isApproved: true,
    });
  }

  /**
   * Create a class withdrawal request (Student)
   * @route POST /api/v1/student/classes/:classId/withdraw
   * @access Private (Student)
   */
  async createWithdrawalRequest(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { classId } = req.params;
      const { reason } = req.body;
      const userId = req.user?.userId;
      const files = req.files;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
        } as APIResponse);
        return;
      }

      if (!reason || reason.trim().length === 0) {
        res.status(400).json({
          success: false,
          message: "Reason is required",
        } as APIResponse);
        return;
      }

      const student = await this.getApprovedStudentForWithdrawal(userId);

      if (!student?.schoolId) {
        res.status(404).json({
          success: false,
          message: "Student profile not found or not approved",
        } as APIResponse);
        return;
      }

      const schoolId = student.schoolId.toString();

      const tokenSchoolId = req.user?.schoolId;
      if (
        tokenSchoolId &&
        mongoose.Types.ObjectId.isValid(tokenSchoolId) &&
        tokenSchoolId !== schoolId
      ) {
        res.status(403).json({
          success: false,
          message: "School context does not match your student profile",
        } as APIResponse);
        return;
      }

      // Validate class exists and student is enrolled
      const classItem = await Class.findOne({
        _id: classId,
        schoolId: new mongoose.Types.ObjectId(schoolId),
        studentIds: student._id,
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or student not enrolled in this class",
        } as APIResponse);
        return;
      }

      // Check if there's already a pending withdrawal request
      const existingRequest = await ClassWithdrawalRequest.findOne({
        studentId: student._id,
        classId: new mongoose.Types.ObjectId(classId),
        status: WITHDRAWAL_REQUEST_STATUS.PENDING,
      });

      if (existingRequest) {
        res.status(400).json({
          success: false,
          message:
            "You already have a pending withdrawal request for this class",
        } as APIResponse);
        return;
      }

      // Process uploaded files (images)
      const attachments =
        files?.map(file => ({
          fileName: file.filename,
          originalName: file.originalname,
          fileSize: file.size,
          mimeType: file.mimetype,
          fileUrl: `/uploads/documents/${file.filename}`,
          uploadedAt: new Date(),
        })) || [];

      // Create withdrawal request
      const withdrawalRequest = new ClassWithdrawalRequest({
        studentId: student._id,
        classId: new mongoose.Types.ObjectId(classId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        semesterId: classItem.semesterId,
        reason: reason.trim(),
        attachments,
        status: WITHDRAWAL_REQUEST_STATUS.PENDING,
      });

      await withdrawalRequest.save();

      logger.info(`Class withdrawal request created: ${withdrawalRequest.id}`, {
        studentId: student.id,
        classId,
        schoolId,
      });

      res.status(201).json({
        success: true,
        message: "Withdrawal request submitted successfully",
        data: {
          withdrawalRequest: {
            id: withdrawalRequest.id,
            classId: withdrawalRequest.classId,
            reason: withdrawalRequest.reason,
            status: withdrawalRequest.status,
            attachments: withdrawalRequest.attachments,
            createdAt: withdrawalRequest.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Create withdrawal request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create withdrawal request",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get student's withdrawal requests
   * @route GET /api/v1/student/withdrawal-requests
   * @access Private (Student)
   */
  async getStudentWithdrawalRequests(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { status, page = 1, limit = 10 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student?.schoolId) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const schoolId = student.schoolId.toString();

      const tokenSchoolId = req.user?.schoolId;
      if (
        tokenSchoolId &&
        mongoose.Types.ObjectId.isValid(tokenSchoolId) &&
        tokenSchoolId !== schoolId
      ) {
        res.status(403).json({
          success: false,
          message: "School context does not match your student profile",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        studentId: student._id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (status) {
        filterQuery.status = status;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const requests = await ClassWithdrawalRequest.find({ ...filterQuery })
        .populate("class", "className classCode courseName")
        .populate("approvedBy", "fullName email")
        .populate("rejectedBy", "fullName email")
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await ClassWithdrawalRequest.countDocuments({
        ...filterQuery,
      });

      res.status(200).json({
        success: true,
        message: "Withdrawal requests retrieved successfully",
        data: {
          requests: requests.map(req => {
            const reqDoc = req as any;
            return {
              id: req.id,
              class: reqDoc.class
                ? {
                    id: reqDoc.class.id || reqDoc.class._id,
                    className: reqDoc.class.className,
                    classCode: reqDoc.class.classCode,
                    courseName: reqDoc.class.courseName,
                  }
                : null,
              reason: req.reason,
              status: req.status,
              attachments: req.attachments,
              approvedBy: reqDoc.approvedBy
                ? {
                    id: reqDoc.approvedBy.id || reqDoc.approvedBy._id,
                    fullName: reqDoc.approvedBy.fullName,
                    email: reqDoc.approvedBy.email,
                  }
                : null,
              approvedAt: req.approvedAt,
              rejectedBy: reqDoc.rejectedBy
                ? {
                    id: reqDoc.rejectedBy.id || reqDoc.rejectedBy._id,
                    fullName: reqDoc.rejectedBy.fullName,
                    email: reqDoc.rejectedBy.email,
                  }
                : null,
              rejectedAt: req.rejectedAt,
              rejectionReason: req.rejectionReason,
              createdAt: req.createdAt,
            };
          }),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get student withdrawal requests error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve withdrawal requests",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get withdrawal requests for teacher's classes
   * @route GET /api/v1/teacher/withdrawal-requests
   * @access Private (Teacher)
   */
  async getTeacherWithdrawalRequests(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { classId, status, page = 1, limit = 10 } = req.query;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      // Find teacher
      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        isApproved: true,
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found or not approved",
        } as APIResponse);
        return;
      }

      // Get teacher's classes
      const teacherClasses = await Class.find({
        schoolId: new mongoose.Types.ObjectId(schoolId),
        teacherIds: teacher._id,
      }).select("_id");

      const classIds = teacherClasses.map(c => c._id);

      if (classIds.length === 0) {
        res.status(200).json({
          success: true,
          message: "No classes found for this teacher",
          data: {
            requests: [],
            pagination: {
              page: 1,
              limit: 10,
              total: 0,
              pages: 0,
            },
          },
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        classId: { $in: classIds },
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (classId) {
        filterQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      if (status) {
        filterQuery.status = status;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const requests = await ClassWithdrawalRequest.find({ ...filterQuery })
        .populate("student", "fullName studentId profilePicture")
        .populate("class", "className classCode courseName")
        .populate("approvedBy", "fullName email")
        .populate("rejectedBy", "fullName email")
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await ClassWithdrawalRequest.countDocuments({
        ...filterQuery,
      });

      res.status(200).json({
        success: true,
        message: "Withdrawal requests retrieved successfully",
        data: {
          requests: requests.map(req => {
            const reqDoc = req as any;
            return {
              id: req.id,
              student: reqDoc.student
                ? {
                    id: reqDoc.student.id || reqDoc.student._id,
                    fullName: reqDoc.student.fullName,
                    studentId: reqDoc.student.studentId,
                    profilePicture: reqDoc.student.profilePicture,
                  }
                : null,
              class: reqDoc.class
                ? {
                    id: reqDoc.class.id || reqDoc.class._id,
                    className: reqDoc.class.className,
                    classCode: reqDoc.class.classCode,
                    courseName: reqDoc.class.courseName,
                  }
                : null,
              reason: req.reason,
              status: req.status,
              attachments: req.attachments,
              approvedBy: reqDoc.approvedBy
                ? {
                    id: reqDoc.approvedBy.id || reqDoc.approvedBy._id,
                    fullName: reqDoc.approvedBy.fullName,
                    email: reqDoc.approvedBy.email,
                  }
                : null,
              approvedAt: req.approvedAt,
              rejectedBy: reqDoc.rejectedBy
                ? {
                    id: reqDoc.rejectedBy.id || reqDoc.rejectedBy._id,
                    fullName: reqDoc.rejectedBy.fullName,
                    email: reqDoc.rejectedBy.email,
                  }
                : null,
              rejectedAt: req.rejectedAt,
              rejectionReason: req.rejectionReason,
              createdAt: req.createdAt,
            };
          }),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get teacher withdrawal requests error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve withdrawal requests",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Approve withdrawal request (Teacher/School Admin)
   * @route PUT /api/v1/school/withdrawal-requests/:id/approve
   * @access Private (Teacher, School Admin)
   */
  async approveWithdrawalRequest(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const userRole = req.user?.role;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid withdrawal request ID",
        } as APIResponse);
        return;
      }

      // Find withdrawal request
      const withdrawalRequest = await ClassWithdrawalRequest.findById(id)
        .populate("class")
        .populate("student");

      if (!withdrawalRequest) {
        res.status(404).json({
          success: false,
          message: "Withdrawal request not found",
        } as APIResponse);
        return;
      }

      // Verify school access
      if (withdrawalRequest.schoolId.toString() !== schoolId) {
        res.status(403).json({
          success: false,
          message: "You don't have access to this withdrawal request",
        } as APIResponse);
        return;
      }

      // If teacher, verify they teach this class
      if (userRole === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });

        if (!teacher) {
          res.status(404).json({
            success: false,
            message: "Teacher profile not found",
          } as APIResponse);
          return;
        }

        const classItem = await Class.findOne({
          _id: withdrawalRequest.classId,
          teacherIds: teacher._id,
        });

        if (!classItem) {
          res.status(403).json({
            success: false,
            message: "You don't teach this class",
          } as APIResponse);
          return;
        }
      }

      // Check if already processed
      if (!withdrawalRequest.isPending()) {
        res.status(400).json({
          success: false,
          message: `Withdrawal request is already ${withdrawalRequest.status}`,
        } as APIResponse);
        return;
      }

      // Approve request
      await withdrawalRequest.approve(new mongoose.Types.ObjectId(userId));

      // Unenroll student from class
      const classItem = await Class.findById(withdrawalRequest.classId);
      if (classItem) {
        await classItem.unenrollStudent(withdrawalRequest.studentId);
      }

      // Update enrollment status
      const enrollment = await Enrollment.findOne({
        studentId: withdrawalRequest.studentId,
        courseOfferingId: withdrawalRequest.classId,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (enrollment) {
        enrollment.status = ENROLLMENT_STATUS.WITHDRAWN;
        enrollment.withdrawalDate = new Date();
        enrollment.withdrawalReason = withdrawalRequest.reason;
        await enrollment.save();
      }

      logger.info(`Withdrawal request approved: ${id}`, {
        withdrawalRequestId: id,
        classId: withdrawalRequest.classId,
        studentId: withdrawalRequest.studentId,
        approvedBy: userId,
      });

      res.status(200).json({
        success: true,
        message: "Withdrawal request approved successfully",
        data: {
          withdrawalRequest: {
            id: withdrawalRequest.id,
            status: withdrawalRequest.status,
            approvedAt: withdrawalRequest.approvedAt,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Approve withdrawal request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to approve withdrawal request",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Reject withdrawal request (Teacher/School Admin)
   * @route PUT /api/v1/school/withdrawal-requests/:id/reject
   * @access Private (Teacher, School Admin)
   */
  async rejectWithdrawalRequest(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { id } = req.params;
      const { rejectionReason } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const userRole = req.user?.role;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid withdrawal request ID",
        } as APIResponse);
        return;
      }

      // Find withdrawal request
      const withdrawalRequest = await ClassWithdrawalRequest.findById(id);

      if (!withdrawalRequest) {
        res.status(404).json({
          success: false,
          message: "Withdrawal request not found",
        } as APIResponse);
        return;
      }

      // Verify school access
      if (withdrawalRequest.schoolId.toString() !== schoolId) {
        res.status(403).json({
          success: false,
          message: "You don't have access to this withdrawal request",
        } as APIResponse);
        return;
      }

      // If teacher, verify they teach this class
      if (userRole === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });

        if (!teacher) {
          res.status(404).json({
            success: false,
            message: "Teacher profile not found",
          } as APIResponse);
          return;
        }

        const classItem = await Class.findOne({
          _id: withdrawalRequest.classId,
          teacherIds: teacher._id,
        });

        if (!classItem) {
          res.status(403).json({
            success: false,
            message: "You don't teach this class",
          } as APIResponse);
          return;
        }
      }

      // Check if already processed
      if (!withdrawalRequest.isPending()) {
        res.status(400).json({
          success: false,
          message: `Withdrawal request is already ${withdrawalRequest.status}`,
        } as APIResponse);
        return;
      }

      // Reject request
      await withdrawalRequest.reject(
        new mongoose.Types.ObjectId(userId),
        rejectionReason || "Withdrawal request rejected",
      );

      // Notify student
      const withdrawalDoc =
        await ClassWithdrawalRequest.findById(id).populate("class");
      const classDoc = withdrawalDoc as any;
      const className = classDoc.class?.className || "class";
      await notificationService.notifyWithdrawalRequestStatus(
        withdrawalRequest.studentId,
        new mongoose.Types.ObjectId(schoolId),
        withdrawalRequest.classId,
        false,
        withdrawalRequest.id,
        className,
      );

      logger.info(`Withdrawal request rejected: ${id}`, {
        withdrawalRequestId: id,
        rejectedBy: userId,
      });

      res.status(200).json({
        success: true,
        message: "Withdrawal request rejected successfully",
        data: {
          withdrawalRequest: {
            id: withdrawalRequest.id,
            status: withdrawalRequest.status,
            rejectedAt: withdrawalRequest.rejectedAt,
            rejectionReason: withdrawalRequest.rejectionReason,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Reject withdrawal request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to reject withdrawal request",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }
}

export const classWithdrawalController = new ClassWithdrawalController();
